import { Component, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PurchaseOrderService } from 'src/app/services/purchase-order.service';
import { ResponseObject } from 'src/app/models/response-object.model';
import { PaginationComponent } from 'src/app/components/elements/pagination/pagination.component';
import { PurchaseOrderReceivedService } from 'src/app/services/purchase-order-received.service';
import { PurchasesAssociationService } from 'src/app/services/purchases-association.service';
import { forkJoin } from 'rxjs';
import { VendorResponse } from 'src/app/models/vendor.model';

export interface ProviderData {
  id: number;
  name: string;
  nit: string;
}

export interface PurchaseOrder {
  id: number;
  number: number;
  creationDate: number;
  deliveryDate: number;
  provider: number;
  description: string;
  enteredIntoInventory: boolean;
  providerData: VendorResponse;
}

export interface PurchaseOrderDetail {
  id: number;
  purchaseOrder: number;
  product: number;
  warehouse: number;
  quantity: number;   // solicitado
  toReceive: number;  // solicitado (esperado)
  cost: number;
  quantityReceived: number;
  statusType: number;
  statusName: string;
  productData: {
    id: number;
    code: string;
    name: string;
  };
  warehouseData: {
    id: number;
    code: string;
    name: string;
  }
}

// VM para UI
export type ReceiveType = 'ok' | 'miss' | null;
export interface ReceiveRowVm extends PurchaseOrderDetail {
  type: ReceiveType;          // ok/miss/null
  userQuantity: number | null; // lo que llegó (editable cuando miss)
  comment?: string;           // opcional (solo UI)
}

// REQUEST exacto del backend
export interface PurchaseOrderReceivedDetailRequest {
  product: number;
  toReceive: number; // cantidad RECIBIDA realmente
}

export interface PurchaseOrderReceivedRequest {
  purchaseOrder: number;
  pilot: string;
  pilotLicense: string;
  plateCar: string;
  platePlatform: string;
  container: string;
  details: PurchaseOrderReceivedDetailRequest[];
}

@Component({
  selector: 'app-order-received',
  templateUrl: './order-received.component.html',
  styleUrls: ['./order-received.component.scss']
})
export class OrderReceivedComponent {
  // Órdenes & selección
  // Fuente de datos
  allOrdersOriginal: PurchaseOrder[] = []; // sin filtrar
  // La lista que muestras (se alimenta de pagination.filter)
  allOrders: PurchaseOrder[] = [];
  selectedOrder: PurchaseOrder | null = null;
  detailsVm: ReceiveRowVm[] = [];

  // Form de transporte/piloto
  pilot = '';
  pilotLicense = '';
  plateCar = '';
  platePlatform = '';
  container = '';

  // Estados UI
  loadingOrders = false;
  loadingDetails = false;
  saving = false;

  orderNumberQuery = '';

  pagination = {
    total: [] as PurchaseOrder[],   // dataset *filtrado* actual
    filter: [] as PurchaseOrder[],  // página actual
    pageSize: 10,
    pageIndex: 0,
    startElement: 0,
    lastElement: 0
  };

  allDetails: { [key: number]: PurchaseOrderDetail[] } = {};
  showAssociated = true;

  productQuery = '';
  filteredDetails: ReceiveRowVm[] = [];

  @ViewChild(PaginationComponent) paginator!: PaginationComponent;

  constructor(
    private purchaseOrderService: PurchaseOrderService,
    private purchasesAssociationService: PurchasesAssociationService,
    private toast: ToastrService,
    private purchaseOrderReceivedService: PurchaseOrderReceivedService
  ) {}

  ngOnInit(): void {
    if(this.showAssociated){
      this.loadOrders();
    } else {
      this.loadOrdersNotAssociated();
    }
  }

  reloadPage(){
    this.selectedOrder = null;
    this.resetTransportForm();
    if(this.showAssociated){
      this.loadOrders();
    } else {
      this.loadOrdersNotAssociated();
    }
  }

  loadOrders(): void {
    this.loadingOrders = true;
    this.purchasesAssociationService.getRelatedOrdersByType("TRANSIT").subscribe({
      next: (resp:ResponseObject) => {
        this.allOrdersOriginal = resp?.data ?? [];
        // inicial: sin filtro → muestra todo
        // this.applyFilterAndReset();
        this.loadAllDetails();
        this.loadingOrders = false;
      },
      error: () => {
        this.toast.error('Error al obtener las órdenes');
        this.loadingOrders = false;
      }
    });
  }

  loadOrdersNotAssociated(): void {
    this.loadingOrders = true;
    this.purchasesAssociationService.getRelatedOrdersByNoRelated("").subscribe({
      next: (resp:ResponseObject) => {
        this.allOrdersOriginal = resp?.data ?? [];
        // inicial: sin filtro → muestra todo
        // this.applyFilterAndReset();
        this.loadAllDetails();
        this.loadingOrders = false;
      },
      error: () => {
        this.toast.error('Error al obtener las órdenes');
        this.loadingOrders = false;
      }
    });
  }

  selectOrder(order: PurchaseOrder): void {
    if (this.selectedOrder?.id === order.id) return;
    console.log('Seleccionada orden', order);
    
    this.selectedOrder = order;
    this.detailsVm = [];
    // Limpia el form de transporte/piloto para evitar arrastres
    this.resetTransportForm();
    this.loadDetails(order.id);
  }

  resetTransportForm(): void {
    this.pilot = '';
    this.pilotLicense = '';
    this.plateCar = '';
    this.platePlatform = '';
    this.container = '';
  }

  loadDetails(orderId: number): void {
    this.loadingDetails = true;
    this.purchaseOrderService.getOrder(orderId).subscribe({
      next: (resp: ResponseObject) => {
        const details: PurchaseOrderDetail[] = resp?.data ?? [];
        this.detailsVm = details.map(d => ({
          ...d,
          type: null,
          userQuantity: null,
          comment: ''
        }));

        // Inicializa el filtro con lo que venga del filtro de la izquierda
        this.productQuery = this.orderNumberQuery;
        this.applyProductFilter();

        this.loadingDetails = false;
      },
      error: () => {
        this.toast.error('Error al obtener el detalle de la orden');
        this.loadingDetails = false;
      }
    });
  }

  
  applyProductFilter(): void {
    if (!this.productQuery) {
      this.filteredDetails = this.detailsVm;
      return;
    }
    const q = this.productQuery.toLowerCase();
    this.filteredDetails = this.detailsVm.filter(d =>
      d.productData?.name?.toLowerCase().includes(q) ||
      d.productData?.code?.toLowerCase().includes(q)
    );
    console.log(this.filteredDetails);
  }


  // Acciones por fila
  markOk(row: ReceiveRowVm): void {
    row.type = 'ok';
    row.userQuantity = (row.quantity - row.toReceive);
  }

  markMiss(row: ReceiveRowVm): void {
    row.type = 'miss';
    row.userQuantity = (row.quantity - row.toReceive);
  }

  // Métricas y helpers
  get decidedCount(): number {
    return this.detailsVm.filter(r => r.type !== null).length;
  }
  get missCount(): number {
    return this.detailsVm.filter(r => r.type === 'miss').length;
  }
  get okCount(): number {
    return this.detailsVm.filter(r => r.type === 'ok').length;
  }
  difference(row: ReceiveRowVm): number {
    const uq = typeof row.userQuantity === 'number' ? row.userQuantity : 0;
    if (uq > (row.quantity - row.toReceive)) return uq - (row.quantity - row.toReceive);
    return (row.quantity - row.toReceive) - uq;
  }

  // Validaciones
  private isNonEmpty(s: string): boolean {
    return !!s && s.trim().length > 0;
  }

  transportFormValid(): boolean {
    // Ajusta si alguno no es obligatorio en tu flujo
    return this.isNonEmpty(this.pilot)
        && this.isNonEmpty(this.pilotLicense)
        && this.isNonEmpty(this.plateCar)
        && this.isNonEmpty(this.platePlatform)
        && this.isNonEmpty(this.container);
  }

  rowValid(row: ReceiveRowVm): boolean {
    if (!row.type) return false;
    if (row.type === 'ok') {
      return row.userQuantity === (row.quantity - row.toReceive);
    }
    const uq = row.userQuantity ?? 0;
    return uq >= 0 && uq <= (row.quantity - row.toReceive);
  }

  allRowsValid(): boolean {
    let valid = this.filteredDetails.length > 0 && this.filteredDetails.every(r => this.rowValid(r));
    //console.log("all rows valid" , valid);
    return valid;
  }

  // Acciones generales
  markAllOk(): void {
    this.filteredDetails.forEach(r => this.markOk(r));
  }

  // Guardar → arma el PurchaseOrderReceivedRequest exacto
  saveReceived(): void {
    if (!this.selectedOrder) {
      this.toast.warning('Selecciona una orden.');
      return;
    }
    if (!this.transportFormValid()) {
      this.toast.warning('Completa los datos del piloto, placas y contenedor.');
      return;
    }
    if (!this.allRowsValid()) {
      this.toast.warning('Revisa las líneas con errores antes de guardar.');
      return;
    }

    const details: PurchaseOrderReceivedDetailRequest[] = this.detailsVm.map(r => ({
      product: r.product,
      // Enviamos lo que REALMENTE llegó:
      toReceive: Number((r.userQuantity ?? 0) + (r.toReceive ?? 0))
    }));

    const payload: PurchaseOrderReceivedRequest = {
      purchaseOrder: this.selectedOrder.id,
      pilot: this.pilot.trim(),
      pilotLicense: this.pilotLicense.trim(),
      plateCar: this.plateCar.trim(),
      platePlatform: this.platePlatform.trim(),
      container: this.container.trim(),
      details
    };

    this.saving = true;
    this.purchaseOrderReceivedService.createReceived(payload).subscribe({
      next: () => {
        this.toast.success('Recepción creada correctamente');
        this.saving = false;
        this.reloadPage();
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'Error al crear la recepción');
        this.saving = false;
      }
    });
  }

  epochToDate(epochMs: number | null | undefined): string {
    if (!epochMs) return '-';
    return new Date(epochMs).toLocaleDateString();
  }

  trackByOrder = (_: number, o: PurchaseOrder) => o.id;
  trackByDetail = (_: number, d: ReceiveRowVm) => d.id;

  get totalMissingUnits(): number {
    return this.detailsVm.reduce((acc, r) => acc + Math.max(0, (r.quantity - r.toReceive) - (r.userQuantity || 0)), 0);
  }

  loadAllDetails(){
    // Creamos un arreglo de observables
  const requests = this.allOrdersOriginal.map((order) =>
    this.purchaseOrderService.getOrder(order.id)
  );

  // Ejecutamos todas en paralelo y esperamos a que terminen
  forkJoin(requests).subscribe({
    next: (responses) => {
      // responses es un array con las respuestas en el mismo orden
      responses.forEach((resp: ResponseObject, index: number) => {
        const orderId = this.allOrdersOriginal[index].id;
        const details: PurchaseOrderDetail[] = resp?.data ?? [];
        this.allDetails[orderId] = details;
      });
      
      this.applyFilterAndReset();
    },
    error: () => {
      this.toast.error('Error al obtener el detalle de las órdenes');
    }
  });
  }

  applyFilterAndReset(): void {
    const q = (this.orderNumberQuery || '').trim();
    let filtered = this.allOrdersOriginal;
    
    if (q) {
      // Coincidencia contiene: convierte a string por si mandas "5" y hay 15, 25, etc.
      filtered = this.allOrdersOriginal.filter(o =>
        o?.number?.toString().includes(q) || this.filterForDetail(q, o.id)
      );
    }

    this.pagination.total = filtered;
    // Reinicia el componente de paginación para recalcular filter
    // Si el ViewChild aún no está, haz un fallback manual:
    if (this.paginator) {
      this.paginator.reset();
    } else {
      this.pagination.pageIndex = 0;
      this.pagination.startElement = 0;
      const temporalLast = Math.min(this.pagination.pageSize, this.pagination.total.length);
      this.pagination.lastElement = temporalLast;
      this.pagination.filter = this.pagination.total.slice(0, temporalLast);
    }

    // Mantén una referencia conveniente si la necesitas en la plantilla
    this.allOrders = this.pagination.filter;
  }

  filterForDetail(value: string, orderId: number){
    const listDetails = this.allDetails[orderId] ?? [];
    value = value.toLowerCase();

    return listDetails.some(detail =>
      detail.productData.name.toLowerCase().includes(value)
    );
  }

  /** Recibe eventos del paginador (no necesitas usar el $event para cliente-side) */
  onPageChanged(_: any): void {
    // El componente de paginación ya actualiza pagination.filter.
    this.allOrders = this.pagination.filter;
  }

  /** Limpia el filtro rápidamente */
  clearOrderFilter(): void {
    this.orderNumberQuery = '';
    this.applyFilterAndReset();
  }

  toggleShowAssociated(show: boolean): void {
    this.showAssociated = show;
    if(this.showAssociated){
      this.loadOrders();
    } else {
      this.loadOrdersNotAssociated();
    }
  }
}